// ============================================================================
// APP.JSX - Main App with i18n and RTL Support
// ============================================================================

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AuthProvider, useAuth } from './lib/AuthContext';

// Import i18n config (must be imported before components)
import './i18n';

// Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import Assessment from './pages/Assessment';
import ResultsPreview from './pages/ResultsPreview';
import ResultsFull from './pages/ResultsFull';
import Dashboard from './pages/Dashboard';
import PaymentSuccess from './pages/PaymentSuccess';
import ActionPlan from './pages/ActionPlan';
import Settings from './pages/Settings';
import AuthCallback from './pages/AuthCallback';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';

// RTL Languages
const RTL_LANGUAGES = ['ar', 'he', 'ur', 'fa'];

// RTL Provider Component
const RTLProvider = ({ children }) => {
  const { i18n } = useTranslation();
  
  useEffect(() => {
    const isRTL = RTL_LANGUAGES.includes(i18n.language);
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
    
    if (isRTL) {
      document.documentElement.classList.add('rtl');
      document.body.classList.add('font-arabic');
    } else {
      document.documentElement.classList.remove('rtl');
      document.body.classList.remove('font-arabic');
    }
  }, [i18n.language]);
  
  return <>{children}</>;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Pro Route Component (requires subscription)
const ProRoute = ({ children }) => {
  const { user, profile, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  if (profile?.subscription_status !== 'active') {
    return <Navigate to="/results" replace />;
  }
  
  return children;
};

// Main App Component
function App() {
  return (
    <AuthProvider>
      <RTLProvider>
        <Router>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/assessment" element={<Assessment />} />
            <Route path="/results" element={<ResultsPreview />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            
            {/* Protected Routes (require login) */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/settings" element={
              <ProtectedRoute>
                <Settings />
              </ProtectedRoute>
            } />
            
            {/* Pro Routes (require subscription) */}
            <Route path="/results/full" element={
              <ProRoute>
                <ResultsFull />
              </ProRoute>
            } />
            <Route path="/action-plan" element={
              <ProRoute>
                <ActionPlan />
              </ProRoute>
            } />
            
            {/* Catch all */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </RTLProvider>
    </AuthProvider>
  );
}

export default App;
